import { useState } from "react";
import { useSearchParams } from "react-router";

import { OrganizationPicker } from "../components/OrganizationPicker.js";
import { Empty, Failure, Panel } from "../components/Panel.js";
import { useResource } from "../hooks.js";
import { apiPost } from "../lib/api.js";
import { type ApiKeyRecord, type Organization } from "../types.js";

const SYSTEM_PATH = "/app/control/apikey";

function KeyTable({
  busy,
  keys,
  onRevoke,
}: {
  busy: boolean;
  keys: ApiKeyRecord[];
  onRevoke: (id: string) => void;
}) {
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-ink/50">
        <tr>
          <th className="pb-2 font-medium">Name</th>
          <th className="pb-2 font-medium">Ends in</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 font-medium">Last used</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => (
          <tr key={key.id} className="border-t border-black/5">
            <td className="py-2">{key.name}</td>
            <td className="py-2 font-mono">{key.label}</td>
            <td className="py-2">{key.status}</td>
            <td className="py-2 text-ink/60">
              {key.lastUsedAt
                ? new Date(key.lastUsedAt).toLocaleString()
                : "never"}
            </td>
            <td className="py-2 text-right">
              {key.status === "active" ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRevoke(key.id)}
                  className="cursor-pointer text-red-700 hover:underline disabled:cursor-not-allowed"
                >
                  Revoke
                </button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * System keys carry `system:*` and belong to no organization.
 *
 * Minting one here is the only path to a system credential, which is what
 * lets an install owner reach the public API with nothing but a sign-in.
 */
function SystemKeys() {
  const keys = useResource<ApiKeyRecord[]>(SYSTEM_PATH);
  const [name, setName] = useState("");
  const [minted, setMinted] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setFailure(undefined);
    try {
      await work();
      keys.reload();
    } catch (error) {
      setFailure((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="System keys">
      <p className="mb-3 text-sm text-ink/60">
        A system key carries every system permission and belongs to no
        organization. Mint one only for an operator or an automation.
      </p>
      <form
        className="flex flex-wrap gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          act(async () => {
            const result = await apiPost<{ key: string }>(SYSTEM_PATH, {
              name: name.trim(),
            });
            setMinted(result.key);
            setName("");
          });
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="What is this key for?"
          className="flex-1 rounded border border-black/15 px-3 py-2"
        />
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="cursor-pointer rounded bg-jay px-4 py-2 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Create
        </button>
      </form>

      {minted ? (
        <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3">
          <p className="mb-2 text-sm font-medium text-amber-900">
            Copy this key now. It is never shown again.
          </p>
          <code className="block break-all rounded bg-white px-3 py-2 font-mono text-sm">
            {minted}
          </code>
          <button
            type="button"
            onClick={() => setMinted(undefined)}
            className="mt-2 cursor-pointer text-sm text-amber-900 underline"
          >
            I have copied it
          </button>
        </div>
      ) : null}

      {failure ? (
        <div className="mt-4">{<Failure>{failure}</Failure>}</div>
      ) : null}

      <div className="mt-4">
        {keys.loading ? <Empty>Loading</Empty> : null}
        {keys.error ? <Failure>{keys.error.message}</Failure> : null}
        {keys.value && keys.value.length === 0 ? (
          <Empty>No system keys yet</Empty>
        ) : null}
        {keys.value && keys.value.length > 0 ? (
          <KeyTable
            busy={busy}
            keys={keys.value}
            onRevoke={(id) => act(() => apiPost(`${SYSTEM_PATH}/${id}/revoke`))}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function OrganizationKeys() {
  const organizations = useResource<Organization[]>(
    "/app/control/organization",
  );
  const [params, setParams] = useSearchParams();
  const organization = params.get("organization") || undefined;
  const path = organization
    ? `/app/control/organization/${organization}/apikey`
    : null;
  const keys = useResource<ApiKeyRecord[]>(path);
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function revoke(id: string) {
    setBusy(true);
    setFailure(undefined);
    try {
      await apiPost(`${path}/${id}/revoke`);
      keys.reload();
    } catch (error) {
      setFailure((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <OrganizationPicker
        organizations={organizations.value || []}
        value={organization}
        onChange={(id) => setParams({ organization: id })}
      />

      {!organization ? <Empty>Choose an organization</Empty> : null}

      {organization ? (
        <Panel title="Keys">
          {failure ? <Failure>{failure}</Failure> : null}
          {keys.loading ? <Empty>Loading</Empty> : null}
          {keys.error ? <Failure>{keys.error.message}</Failure> : null}
          {keys.value && keys.value.length === 0 ? (
            <Empty>No keys yet</Empty>
          ) : null}
          {keys.value && keys.value.length > 0 ? (
            <KeyTable busy={busy} keys={keys.value} onRevoke={revoke} />
          ) : null}
        </Panel>
      ) : null}
    </>
  );
}

export function KeysPage() {
  return (
    <>
      <SystemKeys />
      <OrganizationKeys />
    </>
  );
}
